import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import {
  QueryId,
  TOTAL_PRICE_DEPENDENT_QUERIES,
} from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseMutationAddPreBookingSeatData = Booking.Components['PutSeatResponse'];

interface UseMutationAddPreBookingSeatError {
  errors: any;
  message?: string;
}

interface UseMutationAddPreBookingSeatParameters {
  brand?: Content.Properties.Brand;
  data: Booking.Components['PutSeatRequestBody'];
  invalidationKeys?: QueryId[];
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
}

const useMutationAddPreBookingSeat = () => {
  const locale = getLocale();
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    UseMutationAddPreBookingSeatData,
    UseMutationAddPreBookingSeatError,
    UseMutationAddPreBookingSeatParameters
  > = useMutation(
    async parameters => {
      const fetchResponse = await fetch(
        '/api/booking/pre-booking/seats/add-seat',
        {
          body: JSON.stringify({
            bookingStateId: parameters.parameters.bookingStateId,
            brand: parameters.brand,
            data: parameters.data,
          }),
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'PUT',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('bookingFlow.useMutationAddPreBookingSeat', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      onError: async error => error,
      onSuccess: (_data, parameters) => {
        parameters.invalidationKeys?.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });

        TOTAL_PRICE_DEPENDENT_QUERIES.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });
      },
    },
  );

  return mutation;
};

export default useMutationAddPreBookingSeat;
